import { useState, useRef, useEffect } from "react";
import { useParams } from "react-router";
import { Link } from "react-router";
import { Pencil } from "lucide-react";
import { formatDate, formatDateTime } from "@/lib/format";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  clientsApi,
  clientAddressesApi,
  accountsApi,
  type ClientDto,
  type ClientAddressDto,
  type ClientAuditDto,
  type AccountSummaryDto,
  type UpdateClientRequest,
  type UpdateClientAddressRequest,
  type CreateClientAddressRequest,
} from "@/lib/clients-accounts";
import { DetailPage } from "@/archetypes/DetailPage";
import { DataGrid, type DataGridColumn } from "@/components/data-grid";
import { AccountStatusBadge, type AccountStatusCode } from "@/components/clients-accounts";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "@/hooks/use-toast";
import { ClientQuotesTab } from "./components/ClientQuotesTab";
import { ClientJobsTab } from "./components/ClientJobsTab";
import { EmailThreadTab } from "@/components/email/EmailThreadTab";

// ---------------------------------------------------------------------------
// OverviewTab
// ---------------------------------------------------------------------------

interface ClientDraft {
  firstName: string;
  lastName: string;
  emailAddress: string;
  ccEmailAddress: string;
  phone: string;
  mobilePhone: string;
  fax: string;
  companyName: string;
  departmentName: string;
  branch: string;
  lockCompanyName: boolean;
  showEmail: boolean;
  plainPackaging: boolean;
  hasApprovedCreditAccount: boolean;
}

function draftFromClient(c: ClientDto): ClientDraft {
  return {
    firstName: c.firstName,
    lastName: c.lastName,
    emailAddress: c.emailAddress ?? "",
    ccEmailAddress: c.ccEmailAddress ?? "",
    phone: c.phone ?? "",
    mobilePhone: c.mobilePhone ?? "",
    fax: c.fax ?? "",
    companyName: c.companyName ?? "",
    departmentName: c.departmentName ?? "",
    branch: c.branch ?? "",
    lockCompanyName: c.lockCompanyName,
    showEmail: c.showEmail,
    plainPackaging: c.plainPackaging,
    hasApprovedCreditAccount: c.hasApprovedCreditAccount,
  };
}

function buildUpdateRequest(c: ClientDto, d: ClientDraft, overrides?: Partial<UpdateClientRequest>): UpdateClientRequest {
  return {
    firstName: d.firstName,
    lastName: d.lastName,
    emailAddress: d.emailAddress || null,
    ccEmailAddress: d.ccEmailAddress || null,
    phone: d.phone || null,
    fax: d.fax || null,
    mobilePhone: d.mobilePhone || null,
    companyId: c.companyId,
    companyName: d.companyName || null,
    departmentName: d.departmentName || null,
    branch: d.branch || null,
    lockCompanyName: d.lockCompanyName,
    showEmail: d.showEmail,
    plainPackaging: d.plainPackaging,
    summary: c.summary,
    deliveryAddressId: c.deliveryAddressId,
    invoiceAddressId: c.invoiceAddressId,
    hasApprovedCreditAccount: d.hasApprovedCreditAccount,
    hasWebSiteLogin: c.hasWebSiteLogin,
    ...overrides,
  };
}

function OverviewTab({ clientId, client }: { clientId: number; client: ClientDto }) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<ClientDraft>(() => draftFromClient(client));
  const [summary, setSummary] = useState(client.summary ?? "");
  const debounceRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const patchMut = useMutation({
    mutationFn: (body: UpdateClientRequest) => clientsApi.patch(clientId, body),
    onSuccess: () => {
      toast.success("Saved");
      void qc.invalidateQueries({ queryKey: ["client", clientId] });
      setEditing(false);
    },
    onError: (err: Error) => toast.error(`Save failed: ${err.message}`),
  });

  // Sync summary field & draft if client data refreshes
  useEffect(() => {
    setSummary(client.summary ?? "");
    if (!editing) setDraft(draftFromClient(client));
  }, [client, editing]);

  function handleSummaryBlur() {
    if (summary === (client.summary ?? "")) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      patchMut.mutate(buildUpdateRequest(client, draft, { summary }));
    }, 800);
  }

  const handleCancel = () => {
    setDraft(draftFromClient(client));
    setEditing(false);
  };

  const handleSave = () => {
    patchMut.mutate(buildUpdateRequest(client, draft));
  };

  return (
    <div className="space-y-6">
      {/* Summary Header Strip */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 bg-[#0f3453]/5 border border-[#0f3453]/10 p-4 rounded-xl items-center">
        <div>
          <div className="text-[10px] text-slate-500 font-bold uppercase tracking-wider">Client Name</div>
          <div className="text-sm font-bold text-slate-900 mt-1">{client.fullName}</div>
        </div>
        <div>
          <div className="text-[10px] text-slate-500 font-bold uppercase tracking-wider">Company</div>
          <div className="text-sm font-bold text-slate-900 mt-1">
            {client.companyName ? (
              client.companyId ? (
                <Link
                  to={`/companies/${client.companyId}`}
                  className="text-blue-600 hover:text-blue-800 hover:underline"
                >
                  {client.companyName}
                </Link>
              ) : (
                client.companyName
              )
            ) : (
              "—"
            )}
          </div>
        </div>
        <div>
          <div className="text-[10px] text-slate-500 font-bold uppercase tracking-wider">Email Address</div>
          <div className="text-sm font-bold mt-1 truncate">
            {client.emailAddress ? (
              <a href={`mailto:${client.emailAddress}`} className="text-blue-600 hover:text-blue-800 hover:underline">
                {client.emailAddress}
              </a>
            ) : (
              <span className="text-slate-900">—</span>
            )}
          </div>
        </div>
        <div>
          <div className="text-[10px] text-slate-500 font-bold uppercase tracking-wider">Credit Account</div>
          <div className="mt-1">
            {client.hasApprovedCreditAccount ? (
              <span className="inline-flex items-center gap-1 text-[10px] font-bold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full border border-emerald-200">✓ Approved</span>
            ) : (
              <span className="inline-flex items-center gap-1 text-[10px] font-bold text-slate-500 bg-slate-50 px-2 py-0.5 rounded-full border border-slate-200">— None</span>
            )}
          </div>
        </div>
        <div className="col-span-2 md:col-span-1 flex justify-end">
          {!editing ? (
            <Button
              variant="outline"
              size="icon"
              className="border-transparent bg-info/15 text-primary hover:bg-info/25 shadow-none h-8 w-8 rounded-full flex items-center justify-center"
              onClick={() => setEditing(true)}
              title="Edit Client"
            >
              <Pencil className="h-4 w-4" />
            </Button>
          ) : (
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={handleCancel} disabled={patchMut.isPending} className="h-8">
                Cancel
              </Button>
              <Button size="sm" onClick={handleSave} disabled={patchMut.isPending} className="h-8 bg-[#0f3453] text-white hover:bg-[#1a2f4c]">
                {patchMut.isPending ? "Saving…" : "Save Changes"}
              </Button>
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Left Column */}
        <div className="space-y-6">
          {/* Contact Details Card */}
          <Card className="border border-slate-200 shadow-sm rounded-xl overflow-hidden bg-white">
            <CardHeader className="border-b border-slate-100 bg-slate-50/50 py-3 px-4">
              <CardTitle className="text-xs font-bold text-slate-800 uppercase tracking-wider">Contact Details</CardTitle>
            </CardHeader>
            <CardContent className="p-4">
              <dl className="space-y-3">
                <EditableRow label="First Name" value={draft.firstName} editing={editing} onChange={(v) => setDraft({ ...draft, firstName: v })} />
                <EditableRow label="Last Name" value={draft.lastName} editing={editing} onChange={(v) => setDraft({ ...draft, lastName: v })} />
                <EditableRow label="Email" value={draft.emailAddress} editing={editing} onChange={(v) => setDraft({ ...draft, emailAddress: v })} />
                <EditableRow label="CC Email" value={draft.ccEmailAddress} editing={editing} onChange={(v) => setDraft({ ...draft, ccEmailAddress: v })} />
                <EditableRow label="Phone" value={draft.phone} editing={editing} onChange={(v) => setDraft({ ...draft, phone: v })} />
                <EditableRow label="Mobile" value={draft.mobilePhone} editing={editing} onChange={(v) => setDraft({ ...draft, mobilePhone: v })} />
                <EditableRow label="Fax" value={draft.fax} editing={editing} onChange={(v) => setDraft({ ...draft, fax: v })} />
                <EditableRow label="Department" value={draft.departmentName} editing={editing} onChange={(v) => setDraft({ ...draft, departmentName: v })} />
              </dl>
            </CardContent>
          </Card>

          {/* System Metadata Card */}
          <Card className="border border-slate-200 shadow-sm rounded-xl overflow-hidden bg-white">
            <CardHeader className="border-b border-slate-100 bg-slate-50/50 py-3 px-4">
              <CardTitle className="text-xs font-bold text-slate-800 uppercase tracking-wider">System Metadata</CardTitle>
            </CardHeader>
            <CardContent className="p-4">
              <dl className="space-y-3">
                <ReadOnlyRow label="Created On" value={client.createdOn ? formatDate(client.createdOn) : "—"} />
                <ReadOnlyRow label="Created By" value={client.createdByName ?? "—"} />
                <ReadOnlyRow label="Has Website Login" value={client.hasWebSiteLogin ? "Yes" : "No"} />
                <ReadOnlyRow label="Deleted" value={client.deleted ? "Yes" : "No"} />
              </dl>
            </CardContent>
          </Card>
        </div>

        {/* Right Column */}
        <div className="space-y-6">
          {/* Options Card */}
          <Card className="border border-slate-200 shadow-sm rounded-xl overflow-hidden bg-white">
            <CardHeader className="border-b border-slate-100 bg-slate-50/50 py-3 px-4">
              <CardTitle className="text-xs font-bold text-slate-800 uppercase tracking-wider">Client Settings</CardTitle>
            </CardHeader>
            <CardContent className="p-4">
              <div className="space-y-4">
                <BoolRow id="cl-lock-company" label="Lock Company Name" checked={draft.lockCompanyName} editing={editing} onChange={(v) => setDraft({ ...draft, lockCompanyName: v })} />
                <BoolRow id="cl-show-email" label="Show Email on Dockets" checked={draft.showEmail} editing={editing} onChange={(v) => setDraft({ ...draft, showEmail: v })} />
                <BoolRow id="cl-plain-pkg" label="Plain Packaging" checked={draft.plainPackaging} editing={editing} onChange={(v) => setDraft({ ...draft, plainPackaging: v })} />
                <BoolRow id="cl-approved-credit" label="Approved Credit Account" checked={draft.hasApprovedCreditAccount} editing={editing} onChange={(v) => setDraft({ ...draft, hasApprovedCreditAccount: v })} />
              </div>
            </CardContent>
          </Card>

          {/* Notes & Summary Card */}
          <Card className="border border-slate-200 shadow-sm rounded-xl overflow-hidden bg-white">
            <CardHeader className="border-b border-slate-100 bg-slate-50/50 py-3 px-4">
              <CardTitle className="text-xs font-bold text-slate-800 uppercase tracking-wider">Notes & Summary</CardTitle>
            </CardHeader>
            <CardContent className="p-4">
              <textarea
                className="w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-ring min-h-[120px]"
                value={summary}
                onChange={(e) => setSummary(e.target.value)}
                onBlur={handleSummaryBlur}
                placeholder="Notes about this client…"
                disabled={patchMut.isPending}
              />
              {patchMut.isPending && <p className="mt-1 text-xs text-muted-foreground">Saving…</p>}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}

function EditableRow({
  label, value, editing, onChange,
}: { label: string; value: string; editing: boolean; onChange: (v: string) => void }) {
  const id = `client-field-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`;
  const isEmail = label.toLowerCase().includes("email");
  return (
    <div className="grid grid-cols-[160px_1fr] gap-3 text-sm items-center">
      <Label htmlFor={id} className="text-muted-foreground">{label}</Label>
      {editing ? (
        <Input id={id} value={value} onChange={(e) => onChange(e.target.value)} className="h-8 text-xs" />
      ) : (
        <dd>
          {isEmail && value ? (
            <a href={`mailto:${value}`} className="text-blue-600 hover:text-blue-800 hover:underline">
              {value}
            </a>
          ) : (
            value || "—"
          )}
        </dd>
      )}
    </div>
  );
}

function BoolRow({
  id, label, checked, editing, onChange, disabled
}: { id: string; label: string; checked: boolean; editing: boolean; onChange: (v: boolean) => void; disabled?: boolean }) {
  return (
    <div className="flex items-center justify-between text-sm py-1">
      <Label htmlFor={id} className="text-muted-foreground font-medium">{label}</Label>
      <Switch
        id={id}
        checked={checked}
        onCheckedChange={onChange}
        disabled={disabled || !editing}
      />
    </div>
  );
}

function ReadOnlyRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="grid grid-cols-[160px_1fr] gap-3 text-sm items-center">
      <dt className="text-muted-foreground">{label}</dt>
      <dd>{value}</dd>
    </div>
  );
}

// ---------------------------------------------------------------------------
// AddressesTab
// ---------------------------------------------------------------------------

function AddressesTab({ clientId }: { clientId: number }) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState<ClientAddressDto | null>(null);
  const [newAddr, setNewAddr] = useState({ address: "", city: "", postCode: "" });
  const [showAdd, setShowAdd] = useState(false);

  const { data: addresses, isLoading } = useQuery({
    queryKey: ["client-addresses", clientId],
    queryFn: () => clientAddressesApi.list(clientId),
  });

  const patchMut = useMutation({
    mutationFn: ({ id, body }: { id: number; body: UpdateClientAddressRequest }) =>
      clientAddressesApi.patch(clientId, id, body),
    onSuccess: () => {
      toast.success("Address updated");
      void qc.invalidateQueries({ queryKey: ["client-addresses", clientId] });
      setEditing(null);
    },
  });

  const createMut = useMutation({
    mutationFn: (body: CreateClientAddressRequest) =>
      clientAddressesApi.create(clientId, body),
    onSuccess: () => {
      toast.success("Address added");
      void qc.invalidateQueries({ queryKey: ["client-addresses", clientId] });
      setShowAdd(false);
      setNewAddr({ address: "", city: "", postCode: "" });
    },
  });

  const cols: DataGridColumn<ClientAddressDto>[] = [
    {
      id: "addr",
      header: "Address",
      accessor: (r) =>
        editing?.id === r.id ? (
          <Input
            defaultValue={r.address}
            onBlur={(e) =>
              patchMut.mutate({
                id: r.id,
                body: { address: e.target.value, city: r.city, state: r.state, postCode: r.postCode, clientOwnsAddress: r.clientOwnsAddress },
              })
            }
          />
        ) : (
          <button className="underline text-left" onClick={() => setEditing(r)}>
            {r.formattedAddress ?? r.address}
          </button>
        ),
    },
    { id: "city", header: "City", accessor: (r) => r.city ?? "—" },
    { id: "state", header: "State", accessor: (r) => r.state },
    { id: "postcode", header: "Post code", accessor: (r) => r.postCode ?? "—" },
  ];

  return (
    <div className="space-y-4">
      <DataGrid
        rows={(addresses ?? []).filter((a) => !a.deleted)}
        columns={cols}
        rowKey={(r) => r.id}
        totalRows={(addresses ?? []).filter((a) => !a.deleted).length}
        pageIndex={0}
        pageSize={100}
        loading={isLoading}
        onPageChange={() => {}}
        emptyHeadline="No addresses"
      />
      {showAdd ? (
        <form
          className="flex flex-wrap gap-2 items-end"
          onSubmit={(e) => {
            e.preventDefault();
            createMut.mutate({ address: newAddr.address, city: newAddr.city, postCode: newAddr.postCode, state: null });
          }}
        >
          <div>
            <Label htmlFor="na-addr">Address *</Label>
            <Input id="na-addr" required value={newAddr.address} onChange={(e) => setNewAddr({ ...newAddr, address: e.target.value })} />
          </div>
          <div>
            <Label htmlFor="na-city">City</Label>
            <Input id="na-city" value={newAddr.city} onChange={(e) => setNewAddr({ ...newAddr, city: e.target.value })} />
          </div>
          <div>
            <Label htmlFor="na-pc">Post code</Label>
            <Input id="na-pc" value={newAddr.postCode} onChange={(e) => setNewAddr({ ...newAddr, postCode: e.target.value })} />
          </div>
          <Button type="submit" disabled={createMut.isPending}>Add</Button>
          <Button type="button" variant="outline" onClick={() => setShowAdd(false)}>Cancel</Button>
        </form>
      ) : (
        <Button variant="outline" onClick={() => setShowAdd(true)}>+ Add address</Button>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// AccountsTab
// ---------------------------------------------------------------------------

function AccountsTab({ clientId }: { clientId: number }) {
  const { data, isLoading } = useQuery({
    queryKey: ["accounts-for-client", clientId],
    queryFn: () => accountsApi.list(`clientId=${clientId}`),
  });

  const cols: DataGridColumn<AccountSummaryDto>[] = [
    {
      id: "ref",
      header: "Reference",
      accessor: (r) => (
        <Link to={`/accounts/${r.accountId}`} className="underline text-primary">
          {r.accountReferenceNo}
        </Link>
      ),
    },
    { id: "company", header: "Company", accessor: (r) => r.companyName ?? "—" },
    {
      id: "status",
      header: "Status",
      accessor: (r) => (
        <AccountStatusBadge code={r.accountStatusCode as AccountStatusCode} />
      ),
    },
    {
      id: "balance",
      header: "Balance",
      accessor: (r) => fmt.format(r.currentBalanceOutstanding),
    },
  ];

  return (
    <DataGrid
      rows={data?.items ?? []}
      columns={cols}
      rowKey={(r) => r.accountId}
      totalRows={data?.totalCount ?? 0}
      pageIndex={0}
      pageSize={50}
      loading={isLoading}
      onPageChange={() => {}}
      emptyHeadline="No accounts"
    />
  );
}

const fmt = new Intl.NumberFormat("en-AU", { style: "currency", currency: "AUD" });

// ---------------------------------------------------------------------------
// HistoryTab
// ---------------------------------------------------------------------------

function HistoryTab({ clientId }: { clientId: number }) {
  const { data, isLoading } = useQuery({
    queryKey: ["client-audit", clientId],
    queryFn: () => clientsApi.audit(clientId),
  });

  if (isLoading) return <p className="text-sm text-muted-foreground">Loading…</p>;

  const items = data?.items ?? [];
  if (items.length === 0) return <p className="text-sm text-muted-foreground">No history events.</p>;

  return (
    <ul className="space-y-2 text-sm">
      {items.map((a: ClientAuditDto) => (
        <li key={a.auditRecId} className="flex gap-2">
          <span className="text-muted-foreground whitespace-nowrap">
            {formatDateTime(a.createdOn)}
          </span>
          <span>
            <strong>{a.eventType}</strong>
            {a.eventDescription ? ` — ${a.eventDescription}` : ""}
            {a.userName ? <span className="text-muted-foreground"> ({a.userName})</span> : null}
          </span>
        </li>
      ))}
    </ul>
  );
}

// ---------------------------------------------------------------------------
// ClientDetailPage
// ---------------------------------------------------------------------------

export function ClientDetailPage() {
  const { id = "0" } = useParams();
  const clientId = Number(id);

  const { data: client } = useQuery({
    queryKey: ["client", clientId],
    queryFn: () => clientsApi.get(clientId),
  });

  const tabs = client
    ? [
        {
          id: "overview",
          label: "Overview",
          content: <OverviewTab clientId={clientId} client={client} />,
        },
        {
          id: "addresses",
          label: "Addresses",
          content: <AddressesTab clientId={clientId} />,
        },
        {
          id: "accounts",
          label: "Accounts",
          content: <AccountsTab clientId={clientId} />,
        },
        {
          id: "quotes",
          label: "Quotes",
          content: <ClientQuotesTab clientId={clientId} />,
        },
        {
          id: "jobs",
          label: "Jobs",
          content: <ClientJobsTab clientId={clientId} />,
        },
        {
          id: "emails",
          label: "Emails",
          content: <EmailThreadTab entityType="Client" entityId={clientId} />,
        },
        {
          id: "history",
          label: "History",
          content: <HistoryTab clientId={clientId} />,
        },
      ]
    : [];

  return (
    <DetailPage
      title={client ? "Client Details" : "Loading…"}
      backTo="/clients"
      headerOverride={null}
      tabs={tabs}
      defaultTab="overview"
      tabActions={undefined}
    />
  );
}
